import { parseError, UploadedFile } from '@common';
import {
  Box,
  Button,
  Center,
  Loader,
  Modal,
  ModalProps,
  Stack,
  Text,
} from '@mantine/core';
import {
  Dropzone,
  FileRejection,
  FileWithPath,
  IMAGE_MIME_TYPE,
  MS_EXCEL_MIME_TYPE,
  MS_POWERPOINT_MIME_TYPE,
  MS_WORD_MIME_TYPE,
  PDF_MIME_TYPE,
} from '@mantine/dropzone';
import { useTranslation } from 'react-i18next';

type UploadFileModalPropsType = {
  uploadedFile: UploadedFile | null;
  isUploading: boolean;
  onDrop: (file: FileWithPath) => void;
  onReject: (file: FileRejection) => void;
  onSave: () => Promise<void>;
  error: string | null;
} & Omit<ModalProps, 'title' | 'onDrop'>;

export const UploadFileModal: React.FC<UploadFileModalPropsType> = ({
  uploadedFile,
  isUploading,
  onDrop,
  onReject,
  onSave,
  error,
  ...props
}) => {
  const { t } = useTranslation();

  const handleDrop = (files: FileWithPath[]) => {
    if (files.length > 0) {
      onDrop(files[0]);
    }
  };

  const handleReject = (files: FileRejection[]) => {
    if (files.length > 0) {
      onReject(files[0]);
    }
  };

  const handleSave = async () => {
    try {
      await onSave();
      props.onClose();
    } catch (e) {
      console.error(parseError(e));
    }
  };

  return (
    <Modal {...props} title={t('UPLOAD_FILE_MODAL.TITLE')}>
      <Stack>
        <Dropzone
          onDrop={handleDrop}
          onReject={handleReject}
          multiple={false}
          maxSize={10 * 1024 * 1024} // 10MB
          accept={[
            ...IMAGE_MIME_TYPE,
            ...PDF_MIME_TYPE,
            ...MS_WORD_MIME_TYPE,
            ...MS_EXCEL_MIME_TYPE,
            ...MS_POWERPOINT_MIME_TYPE,
          ]}
        >
          <Center mih={200}>
            <Stack align="center" justify="center">
              <Text>{t('UPLOAD_FILE_MODAL.DROP_ZONE_TITLE')}</Text>
              <Text c="dimmed">
                {t('UPLOAD_FILE_MODAL.DROP_ZONE_DESCRIPTION')}
              </Text>
            </Stack>
          </Center>
        </Dropzone>
        <Box h={40}>
          <Text>{uploadedFile?.file.name}</Text>
          {isUploading && <Loader size="xs" />}
          {error && <Text c="red">{error}</Text>}
        </Box>

        <Button onClick={handleSave} disabled={!uploadedFile || isUploading}>
          {t('UPLOAD_FILE_MODAL.UPLOAD_BUTTON')}
        </Button>
      </Stack>
    </Modal>
  );
};
